import React, { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { InstrumentInitialValuesDto } from '@app/shared/models/contracts/project-dto';
import { toPercentageUsingFormatter } from '@app/shared/formatters';
import { EmptyValues } from '@app/shared/constants/empty-values';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  InstrumentType,
  OwnerType,
  WaterfallSectionsErf,
  WaterfallSections,
} from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import { Pwerm2WaterfallPartialTableProps } from '../pwerm-waterfall-utils/pwerm2-waterfall-shared';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import TableSectionValuesPerMultiple from '../../components/TableSectionValuesPerMultiple';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseYearInstrumentValue,
  getWaterfallCaseYearMultipleInstrumentValue,
  getWaterfallCaseYearMultipleValue,
  getWaterfallCaseYearValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import {
  Pwerm2CalculationWaterfallCaseDto,
  Pwerm2CalculationWaterfallDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import {
  selectSlnAndPrefSharesInstrumentsIncludeInitialValues,
  selectPrefShareInstruments,
  selectSlnInstruments,
} from '@app/core/store/capital-structure-slice-selectors';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import useIsErfProject from '@app/shared/hooks/useIsErfProject';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';

export const Pwerm2WaterfallShareholderDebtDistributionsPartialTable: FC<
  Pwerm2WaterfallPartialTableProps
> = ({ columnsCount }): JSX.Element => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();
  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const isErfProject = useIsErfProject();
  const { handleToggle } = useTogglerPwermWaterfall(
    WaterfallSectionsErf.ShareholderDebtDistributions
  );

  const slnInstruments = useAppSelector(selectSlnInstruments);
  const prefShareInstruments = useAppSelector(selectPrefShareInstruments);
  const slnAndPrefSharesInstruments = useAppSelector(
    selectSlnAndPrefSharesInstrumentsIncludeInitialValues
  );
  const sortedSlnAndPrefSharesInstruments = useInstrumentSort(slnAndPrefSharesInstruments);

  const instrumentIdsWithAnyCaseYearValues = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(results.waterfall.cases).forEach(
      (caseItem: Pwerm2CalculationWaterfallCaseDto) => {
        Object.values(caseItem.years).forEach((year) => {
          Object.keys(year.instruments).forEach((instrumentId) => {
            if (year.instruments[instrumentId]) {
              ids.add(instrumentId);
            }
          });
        });
      }
    );
    return ids;
  }, [results.waterfall.cases]);

  const instrumentHasAnyCaseYearValues = (instrumentId: string) =>
    instrumentIdsWithAnyCaseYearValues.has(instrumentId);

  const renderDebtDistributionInstrumentSection = ({
    instrumentNarrative,
    instrumentId,
    initialValues,
    dataTestId,
  }: {
    instrumentNarrative: string;
    instrumentId: string;
    initialValues: Nullable<InstrumentInitialValuesDto>;
    dataTestId: string;
  }) => {
    const couponValue = initialValues?.coupon?.value;
    if (instrumentHasAnyCaseYearValues(instrumentId)) {
      return (
        <TableSectionValuesPerForecast
          title={
            isErfProject
              ? instrumentNarrative
              : `${instrumentNarrative} [${
                  couponValue || couponValue === 0
                    ? toPercentageUsingFormatter(couponValue)
                    : EmptyValues.EnDash
                }]`
          }
          data-testid={dataTestId}
          key={instrumentId}
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => {
            const value = getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) => year.shareholderDebtByInstrument[instrumentId]
            );
            return <CellValue {...amountRules} value={value} />;
          }}
          additionalTitleClassNames="table-primary__cell--indentation"
        />
      );
    }
    return null;
  };

  const renderShareholderDebtInstrumentSection = ({
    instrumentNarrative,
    instrumentId,
  }: {
    instrumentNarrative: string;
    instrumentId: string;
  }) => {
    if (instrumentHasAnyCaseYearValues(instrumentId)) {
      return (
        <>
          <TableSectionValuesPerMultiple
            key={instrumentId}
            title={instrumentNarrative}
            renderCell={({ caseItem, forecast, multipleId }) => {
              const value = getWaterfallCaseYearMultipleInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                multipleId!,
                instrumentId,
                (instrument) => instrument.totalDistributions
              );
              return (
                <CellValue
                  {...amountRules}
                  value={value}
                  dataTestId={`total-distributions-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                />
              );
            }}
          />
          <TableSectionValuesPerForecast
            title={l('_WeightedInstrumentExitDistribution', { instrument: instrumentNarrative })}
            renderCell={({ forecast, caseItem }) => {
              return (
                <CellValue
                  {...amountRules}
                  value={
                    slnAndPrefSharesInstruments.length > 0
                      ? getWaterfallCaseYearInstrumentValue(
                          results,
                          caseItem.caseId,
                          forecast.forecastId,
                          instrumentId,
                          (year) => year.weightedTotalDistributions
                        )
                      : undefined
                  }
                  dataTestId={`weighted-instrument-exit-distribution-cell-${caseItem.caseId}-${forecast.forecastId}-${instrumentId}`}
                />
              );
            }}
            additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
            additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          />
        </>
      );
    }
  };

  const renderShareholderDebtDistributionsByOwnerType = (item: {
    rowTitle: string;
    ownerType: keyof typeof OwnerType;
  }) => {
    return (
      <>
        <TableSectionValuesPerMultiple
          key={item.ownerType}
          title={item.rowTitle}
          renderCell={({ caseItem, forecast, multipleId }) => {
            const total = slnAndPrefSharesInstruments.length
              ? getWaterfallCaseYearMultipleValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  multipleId!,
                  (multiple) => multiple.shareholderDebtDistributionsByOwner[item.ownerType] ?? 0
                )
              : undefined;

            return (
              <CellValue
                {...amountRules}
                value={total}
                dataTestId={`shareholder-debt-distributions-by-owner-cell-${item.ownerType}-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
              />
            );
          }}
        />
        <TableSectionValuesPerForecast
          title={l('_WeightedOwnerDebt', { owner: item.rowTitle })}
          renderCell={({ forecast, caseItem }) => {
            return (
              <CellValue
                {...amountRules}
                value={
                  slnAndPrefSharesInstruments.length > 0
                    ? getWaterfallCaseYearValue(
                        results,
                        caseItem.caseId,
                        forecast.forecastId,
                        (year) => year.weightedDebtDistributionsByOwnerType[item.ownerType]
                      )
                    : undefined
                }
                dataTestId={`weighted-owner-debt-cell-${item.ownerType}-${caseItem.caseId}-${forecast.forecastId}`}
              />
            );
          }}
          additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
        />
      </>
    );
  };

  const ownerLabels: Record<keyof typeof OwnerType, string> = {
    ['Institution']: l('_Sponsor'),
    ['Management']: l('_Management'),
    ['CoInvestor']: l('_CoInvestor'),
    ['Other']: l('_Other'),
  };

  const getSddOwnersPresent = (
    waterfall: Pwerm2CalculationWaterfallDto
  ): (keyof typeof OwnerType)[] => {
    const ownerSort: Record<keyof typeof OwnerType, number> = {
      ['Institution']: 0,
      ['Management']: 1,
      ['CoInvestor']: 2,
      ['Other']: 3,
    };

    const cases = Object.entries(waterfall.cases);
    const years = cases.flatMap(([_, caseItem]) => Object.entries(caseItem.years));
    const allOwners = years.flatMap(([_, year]) => Object.keys(year.shareholderDebtByOwnerType));
    const uniqueOwners = [...new Set(allOwners as (keyof typeof OwnerType)[])];
    uniqueOwners.sort((a, b) => ownerSort[a] - ownerSort[b]);
    return uniqueOwners;
  };

  const presentOwners = getSddOwnersPresent(results.waterfall);

  const sddOwnerSectionsConfig = presentOwners.map((ownerType: keyof typeof OwnerType) => {
    return {
      rowTitle: ownerLabels[ownerType],
      ownerType: ownerType,
    };
  });

  const totalShareholderDebtDistributionsByOwnerType = sddOwnerSectionsConfig.map(
    renderShareholderDebtDistributionsByOwnerType
  );

  return (
    <>
      <tbody data-testid="shareholder-debt-distributions-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_ShareholderDebtDistributionsTitle')}
          id={enumKeyByValue(WaterfallSections, WaterfallSections.ShareholderDebtDistributions)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.ShareholderDebtDistributions]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.ShareholderDebtDistributions],
        })}
        data-testid="shareholder-debt-distributions-section">
        {(slnInstruments.length > 0 || prefShareInstruments.length > 0) && (
          <TableSectionValuesPerForecast
            title={l('_ContractualCarryingValues')}
            renderSpacer={false}
            renderCell={() => <></>}
            testId="contractual-carrying-values-section"
          />
        )}
        {sortedSlnAndPrefSharesInstruments.map((instrument) =>
          renderDebtDistributionInstrumentSection({
            ...instrument,
            dataTestId: `debt-distribution-instrument-${instrument.instrumentId}`,
          })
        )}
        <TableSectionValuesPerForecast
          title={l('_TotalContractualShareholderDebt')}
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              {...amountRules}
              value={
                slnAndPrefSharesInstruments.length > 0
                  ? getWaterfallCaseYearValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      (year) => year.totalShareholderDebt
                    )
                  : undefined
              }
              dataTestId={`total-contractual-shareholder-debt-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
          additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
        />
        <tr>
          <th
            className="table-primary__cell--header-tertiary"
            data-testid="split-of-contractual-debt-header">
            {l('_SplitOfContractualDebt')}
          </th>
          <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
        </tr>
        {slnInstruments.length > 0 && (
          <TableSectionValuesPerForecast
            renderSpacer={false}
            title={l('_ShareholderLoanNotes')}
            renderCell={({ forecast, caseItem }) => (
              <CellValue
                {...amountRules}
                value={getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) =>
                    year.shareholderDebtByInstrumentType[
                      enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes)
                    ]
                )}
                dataTestId={`shareholder-loan-notes-cell-${caseItem.caseId}-${forecast.forecastId}`}
              />
            )}
          />
        )}
        {prefShareInstruments.length > 0 && (
          <TableSectionValuesPerForecast
            title={l('_PreferenceShares')}
            renderSpacer={false}
            renderCell={({ forecast, caseItem }) => (
              <CellValue
                {...amountRules}
                value={getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) =>
                    year.shareholderDebtByInstrumentType[
                      enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)
                    ]
                )}
                dataTestId={`preference-shares-cell-${caseItem.caseId}-${forecast.forecastId}`}
              />
            )}
          />
        )}

        {sddOwnerSectionsConfig.map((owner, index) => {
          const spacer = index === sddOwnerSectionsConfig.length - 1;

          return (
            <TableSectionValuesPerForecast
              key={owner.ownerType}
              title={owner.rowTitle}
              renderSpacer={spacer}
              renderCell={({ forecast, caseItem }) => {
                const value = getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) => year.shareholderDebtByOwnerType[owner.ownerType]
                );

                return (
                  <CellValue
                    {...amountRules}
                    value={value ?? 0}
                    dataTestId={`shareholder-debt-owner-cell-${owner.ownerType}-${caseItem.caseId}-${forecast.forecastId}`}
                  />
                );
              }}
            />
          );
        })}

        {Boolean(slnAndPrefSharesInstruments.length) && (
          <>
            <tr>
              <th className="table-primary__cell--header-tertiary">
                {l('_ShareholderDebtDistributionsTitle')}
              </th>
              <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
            </tr>
            {sortedSlnAndPrefSharesInstruments.map((instrument) => (
              <React.Fragment key={instrument.instrumentId}>
                {renderShareholderDebtInstrumentSection(instrument)}
              </React.Fragment>
            ))}
            <tr>
              <th className="table-primary__cell--header-tertiary">
                {l('_TotalShareholderDebtDistributionsTitle')}
              </th>
              <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
            </tr>
            <TableSectionValuesPerMultiple
              title={l('_TotalExitDistributions')}
              renderCell={({ caseItem, forecast, multipleId }) => {
                const total = getWaterfallCaseYearMultipleValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  multipleId!,
                  (multiple) => multiple.totalShareholderDebtDistributions
                );

                return (
                  <CellValue
                    {...amountRules}
                    value={total}
                    dataTestId={`total-exit-distributions-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                  />
                );
              }}
            />
            <TableSectionValuesPerForecast
              title={l('_WeightedTotalExitDistribution')}
              renderCell={({ forecast, caseItem }) => {
                return (
                  <CellValue
                    {...amountRules}
                    value={
                      slnAndPrefSharesInstruments.length > 0
                        ? getWaterfallCaseYearValue(
                            results,
                            caseItem.caseId,
                            forecast.forecastId,
                            (year) => year.weightedTotalShareholderDebtDistributions
                          )
                        : undefined
                    }
                    dataTestId={`weighted-total-exit-distribution-cell-${caseItem.caseId}-${forecast.forecastId}`}
                  />
                );
              }}
              additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
              additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
            />
            <TableSectionValuesPerMultiple
              title={l('_Shortfall')}
              renderCell={({ caseItem, forecast, multipleId }) => {
                const shortfall =
                  getWaterfallCaseYearMultipleValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    multipleId!,
                    (multiple) => multiple.shareholderDebtShortfall
                  ) * -1; // Multiply by -1 to make the value negative

                return (
                  <CellValue
                    {...amountRules}
                    value={shortfall}
                    dataTestId={`shareholder-debt-shortfall-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
                  />
                );
              }}
            />
            <tr>
              <th className="table-primary__cell--header-tertiary">
                {l('_SplitOfShareholderDebtDistributions')}
              </th>
              <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
            </tr>
            {totalShareholderDebtDistributionsByOwnerType.map((el, idx) => (
              <React.Fragment key={idx}>{el}</React.Fragment>
            ))}
          </>
        )}
      </tbody>
    </>
  );
};
